import React, { useState, useEffect, useCallback } from 'react';
import { Timer, ArrowDown, BarChart3, Minimize2, LoaderCircle } from 'lucide-react';

const defaultConfig = {
  title: 'Consolidating ETFs into Stock Portfolio',
  subtitle: 'Reducing to Meaningful Holdings',
  phases: [
    {
      icon: LoaderCircle,
      label: 'Merging Stocks from ETFs',
      countSuffix: 'Dropping Redundant Stocks'
    },
    {
      icon: BarChart3,
      label: 'Matching Sectors to Original Set',
      countSuffix: 'Sectors Matched'
    }
  ],
  colors: {
    primary: 'from-blue-400 to-purple-600',
    secondary: 'text-blue-400',
    accent: 'text-purple-400'
  }
};

const WordCloudAnimation = ({
  spinning,
  words = [],
  highlightColors = {
    keep: 'text-green-600',
    remove: 'text-red-600',
    neutral: 'text-blue-400'
  }
}) => {
  const [particles, setParticles] = useState([]);

  const generateParticle = useCallback(() => {
    const word = words[Math.floor(Math.random() * words.length)] || 'DATA';
    const status = Math.random() > 0.6 ? 'keep' : 'remove';
    const size = Math.random() * 1.5 + 1;
    const startPosition = {
      x: Math.random() * 100,
      y: 120
    };
    const endPosition = {
      x: Math.random() * 100,
      y: -20
    };

    return {
      id: Date.now() + Math.random(),
      word,
      status,
      size,
      startPosition,
      endPosition,
      opacity: 0,
      progress: 0
    };
  }, [words]);

  useEffect(() => {
    if (!spinning) return;

    setParticles(Array(10).fill(null).map(generateParticle));

    const addParticleInterval = setInterval(() => {
      setParticles(prev => {
        const filtered = prev.filter(p => p.progress < 1);
        return [...filtered, generateParticle()];
      });
    }, 300);

    const animationInterval = setInterval(() => {
      setParticles(prev =>
        prev.map(particle => ({
          ...particle,
          progress: particle.progress + 0.01,
          opacity: particle.progress < 0.2
            ? particle.progress * 5
            : particle.progress > 0.8
              ? (1 - particle.progress) * 5
              : 1
        }))
      );
    }, 16);

    return () => {
      clearInterval(addParticleInterval);
      clearInterval(animationInterval);
    };
  }, [spinning, generateParticle]);

  return (
    <div className="relative w-full h-full overflow-hidden bg-gradient-to-b from-gray-950 via-black to-gray-950 rounded-lg">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-transparent to-gray-950 z-10" />

      <div className="relative h-full w-full">
        {particles.map(particle => {
          const currentX = particle.startPosition.x +
            (particle.endPosition.x - particle.startPosition.x) * particle.progress;
          const currentY = particle.startPosition.y +
            (particle.endPosition.y - particle.startPosition.y) * particle.progress;

          return (
            <div
              key={particle.id}
              className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-75
                ${highlightColors[particle.status] || highlightColors.neutral}
              `}
              style={{
                left: `${currentX}%`,
                top: `${currentY}%`,
                opacity: particle.opacity,
                fontSize: `${particle.size}rem`,
                transform: `translate(-50%, -50%) scale(${particle.size})`,
              }}
            >
              <div className="font-mono font-bold whitespace-nowrap">
                {particle.word}
              </div>
            </div>
          );
        })}
      </div>

      <div className="absolute inset-0 backdrop-blur-[1px]" />
    </div>
  );
};

const ProgressiveComparison = ({
  progress,
  leftLabel = "ETF",
  rightLabel = "Filtered",
  matchLabel = "Match"
}) => {
  const barCount = 20;

  return (
    <div className="relative h-full w-full bg-gradient-to-b from-gray-950 via-black to-gray-950 rounded-xl overflow-hidden">
      <div className="absolute inset-0 flex">
        <div className="w-1/2 bg-blue-500/[0.02]"></div>
        <div className="w-1/2 bg-purple-500/[0.02]"></div>
      </div>

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-4/5 h-48 flex items-end gap-1">
          {Array.from({ length: barCount }).map((_, i) => {
            const shouldShow = (i / barCount) * 100 <= progress;
            const height = shouldShow ? 40 + Math.random() * 120 : 0;

            return (
              <div
                key={i}
                className="flex-1 transition-all duration-500"
                style={{
                  height: `${height}px`,
                  background: `linear-gradient(180deg,
                    ${i < barCount/2 ? 'rgba(96, 165, 250, 0.3)' : 'rgba(139, 92, 246, 0.3)'},
                    ${i < barCount/2 ? 'rgba(96, 165, 250, 0.1)' : 'rgba(139, 92, 246, 0.1)'}
                  )`
                }}
              />
            );
          })}
        </div>
      </div>

      <div className="absolute inset-0 flex justify-between items-center px-12">
        <div
          className="text-2xl font-bold text-blue-400"
          style={{
            opacity: progress / 100,
            transform: `scale(${progress / 100})`,
            transition: 'all 0.5s ease-out'
          }}
        >
          {leftLabel}
        </div>
        <div
          className="text-2xl font-bold text-purple-400"
          style={{
            opacity: progress / 100,
            transform: `scale(${progress / 100})`,
            transition: 'all 0.5s ease-out'
          }}
        >
          {rightLabel}
        </div>
      </div>

      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="bg-gray-950/80 backdrop-blur-sm px-6 py-3 rounded-full border border-gray-800"
          style={{
            opacity: progress / 100,
            transform: `scale(${progress / 100})`,
            transition: 'all 0.5s ease-out'
          }}
        >
          <div className="text-3xl font-bold text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
            {progress.toFixed(1)}% {matchLabel}
          </div>
        </div>
      </div>
    </div>
  );
};

const LoadingAnimation = ({
  words = [],
  seconds = 5,
  maxInteger = 1000,
  onFinish,
  config = defaultConfig,
  phases = [],
  currentPhase = 0,
  onPhaseComplete,
  highlightColors,
  comparisonLabels = {
    left: 'ETF Portfolio',
    right: 'Stock Portfolio',
    match: 'Risk Match'
  }
}) => {
  const [progress, setProgress] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [spinning, setSpinning] = useState(true);
  const [phase, setPhase] = useState(currentPhase);

  useEffect(() => {
    const totalUpdates = seconds * 60; // Smoother updates
    const interval = (seconds * 1000) / totalUpdates;

    const intervalId = setInterval(() => {
      setProgress(prev => {
        const newProgress = Math.min(prev + (100 / totalUpdates), 100);
        if (newProgress >= 100) {
          setSpinning(false);
          if (onPhaseComplete) {
            onPhaseComplete(phase);
          }
          onFinish?.();
          clearInterval(intervalId);
        }
        setProcessedCount(Math.floor((newProgress / 100) * maxInteger));
        return newProgress;
      });
    }, interval);

    return () => clearInterval(intervalId);
  }, [seconds, maxInteger, onFinish, phase, phases.length, onPhaseComplete]);

  const activePhase = phases[phase] || defaultConfig.phases[0];

  return (
    <div className="w-full h-screen p-8 flex flex-col"
         style={{
           background: 'radial-gradient(circle at center, rgb(3, 7, 18) 0%, rgb(0, 0, 0) 50%, rgb(3, 7, 18) 100%)'
         }}>
      <div className="text-center mb-8">
        <h1 className={`text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r ${config.colors.primary}`}>
          {config.title}
        </h1>
        <p className={config.colors.secondary}>{config.subtitle}</p>
      </div>

      <div className="flex-1 mb-8">
        <div className="bg-gradient-to-b from-gray-950 to-black rounded-xl p-8 border border-gray-800/30 shadow-2xl shadow-blue-500/5 h-full">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center">
              <activePhase.icon className="mr-2 text-blue-400" />
              <span className="text-xl font-semibold text-white">{activePhase.label}</span>
            </div>
            <div className="text-right text-gray-400 font-mono">
              <span className="text-blue-400 text-xl">{processedCount}</span>
              <span className="text-lg"> / {maxInteger} {activePhase.countSuffix}</span>
            </div>
          </div>

          <div className="flex justify-center gap-4 h-64">
            <div className="w-full bg-gradient-to-b from-gray-950 via-black to-gray-950 rounded-xl overflow-hidden shadow-inner relative">
              <WordCloudAnimation
                spinning={spinning}
                words={words}
                highlightColors={highlightColors}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1">
        <div className="bg-gradient-to-b from-gray-950 to-black rounded-xl p-8 border border-gray-800/30 shadow-2xl shadow-blue-500/5 h-full">
          <ProgressiveComparison
            progress={progress}
            leftLabel={comparisonLabels.left}
            rightLabel={comparisonLabels.right}
            matchLabel={comparisonLabels.match}
          />
        </div>
      </div>

      <div className="mt-8">
        <div className="w-full bg-gray-950 rounded-full h-3 overflow-hidden border border-gray-900">
          <div
            className="h-full transition-all duration-300 ease-out relative"
            style={{
              width: `${progress}%`,
              background: 'linear-gradient(90deg, rgba(96, 165, 250, 0.9), rgba(139, 92, 246, 0.9))'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
          </div>
        </div>
        <div className="text-center text-gray-400 mt-2">
          {progress < 100 ? 'Processing...' : 'Phase Complete'}
        </div>
      </div>
    </div>
  );
};

export default LoadingAnimation;